import { SelectChangeEvent } from '@mui/material';
import { useEffect, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '../../../../lib/store';
import { ManageProjectResponse } from '../../../admin-projects/types/response';
import {
  useGetConditionAreasQuery,
  useGetOrganizationsQuery,
  useGetManageProjectsQuery,
} from '../../api/createProjectApiSlice';
import { setConditionArea, setHealthSystem } from '../../state/createProjectSlice';
import { HealthSystem } from '../../state/createProjectSlice';
import { selectConditionArea, selectHealthSystem, selectProjectName } from '../../state/selector';
import { ConditionAreaResponse } from '../../types/response';

interface UseBasicStepResults {
  projectName: string;
  conditionAreaOptions: { value: string; displayValue: string }[];
  isConditionAreasLoading: boolean;
  handleConditionAreaChange: (event: SelectChangeEvent<unknown>) => void;
  conditionArea: ConditionAreaResponse | null;
  organizationOptions: { value: string; displayValue: string }[];
  handleHealthSystemNameChange: (event: SelectChangeEvent<unknown>) => void;
  isOrganizationsLoading: boolean;
  healthSystem: HealthSystem | null;
  duplicateError: string | null;
}

interface UseBasicStepProps {
  setRequiredFieldsFilled: (fieldsFilled: boolean) => void;
}

export const useBasicStep = ({
  setRequiredFieldsFilled,
}: UseBasicStepProps): UseBasicStepResults => {
  const dispatch = useAppDispatch();

  const {
    data: conditionAreas,
    isLoading: isConditionAreasLoading,
    isFetching: isConditionAreasFetching,
  } = useGetConditionAreasQuery();
  const conditionAreaOptions = conditionAreas?.data.map((option) => ({
    value: option.id,
    displayValue: option.label,
  }));

  const conditionArea = useAppSelector(selectConditionArea);
  const handleConditionAreaChange = (event: SelectChangeEvent<unknown>) => {
    const value = event.target.value as string;
    const selectedConditionArea = conditionAreas?.data.find((area) => area.id === value);
    if (selectedConditionArea) {
      dispatch(
        setConditionArea({ id: selectedConditionArea.id, label: selectedConditionArea.label })
      );
    }
  };

  const {
    data: organizations,
    isLoading: isOrganizationsLoading,
    isFetching: isOrganizationsFetching,
  } = useGetOrganizationsQuery();
  const organizationOptions = organizations?.data.map((option) => ({
    value: option.id,
    displayValue: option.label,
  }));
  const healthSystem = useAppSelector(selectHealthSystem);
  const handleHealthSystemNameChange = (event: SelectChangeEvent<unknown>) => {
    const value = event.target.value as string;
    const selectedOrganization = organizations?.data.find((org) => org.id === value);
    if (selectedOrganization) {
      dispatch(setHealthSystem({ id: selectedOrganization.id, label: selectedOrganization.label }));
    }
  };

  const projectName = useAppSelector(selectProjectName);

  const { data: existingProjects } = useGetManageProjectsQuery();

  const duplicateError = useMemo(() => {
    const projects = existingProjects?.data ?? [];
    if (!projects.length) return null;

    const trimmedName = projectName.trim().toLowerCase();

    if (
      trimmedName &&
      projects.some((p: ManageProjectResponse) => p.projectName.toLowerCase() === trimmedName)
    ) {
      return 'createProjectWizard.step.basics.duplicate.name';
    }
    return null;
  }, [projectName, existingProjects]);

  useEffect(() => {
    const allFieldsFilled = Boolean(conditionArea?.id && healthSystem?.id && projectName.trim());
    setRequiredFieldsFilled(allFieldsFilled && !duplicateError);
  }, [conditionArea, healthSystem, projectName, duplicateError]);

  return {
    projectName,
    conditionAreaOptions: conditionAreaOptions ?? [],
    isConditionAreasLoading: isConditionAreasFetching || isConditionAreasLoading,
    handleConditionAreaChange,
    conditionArea: conditionArea ?? null,
    organizationOptions: organizationOptions ?? [],
    handleHealthSystemNameChange,
    isOrganizationsLoading: isOrganizationsFetching || isOrganizationsLoading,
    healthSystem: healthSystem ?? null,
    duplicateError,
  };
};
